if(!customElements.get('immersive-carousel')) {
	customElements.define('immersive-carousel',
		class ImmersiveCarousel extends HTMLElement {
			constructor() {
				super();
				this.setAttribute('role','region');
				this.setAttribute('aria-roledescription','carousel');
				this.setAttribute('aria-label','Image Carousel');
				this.slowTransitions = ['tw-transform-all','tw-duration-1500','md:tw-duration-3000','tw-ease-in-out'];
				this.quickTransitions = ['tw-transform-all','tw-duration-500','tw-ease-out'];
				this.currentItem = 0;
				this.positionInfo = this.querySelector('.carousel-position-info');
				this.nextButton = this.querySelector('.next-button');
				this.textBlurbs = this.querySelectorAll('.text-blurbs > div');
				this.imagesWrapper = this.querySelector('.images');
				this.images = this.querySelectorAll('.images > div');
				this.itemCount = this.textBlurbs.length;
				this.details = this.querySelectorAll('.details > *');
				this.positionInfo?.setAttribute('aria-live','polite');
				this.imagesWrapper?.setAttribute('role','list');
				this.nextButton.addEventListener('click', this.selectNext.bind(this));
				if (this.itemCount > 0) this.setSelected(0);
				this.images.forEach((image,i) => {
					image.setAttribute('aria-label',`Slide ${i+1} of ${this.itemCount}`);
					image.setAttribute('role','listitem group');
					const detailsButton = image.querySelector('.toggle-details');
					if (detailsButton) {
						detailsButton.addEventListener('click', this.toggleDetails.bind(this,i));
						detailsButton.setAttribute('aria-controls',`slide-details-${i}`);
						detailsButton.setAttribute('aria-expanded','false');
					}
				});
				this.details.forEach((panel,i)=>{panel.id ||= `slide-details-${i}`;});
				document.addEventListener('DOMContentLoaded', this.setTransitions.bind(this,{slow:true}));
			}
			selectNext() { this.setSelected((this.currentItem + 1) % this.itemCount); }
			toggleDetails(selected) { this.detailsOpen ? this.closeDetails() : this.openDetails(selected); }
			openDetails(selected) {
				this.detailsOpen = true;
				this.setTransitions({slow:false});
				this.setSelected(selected);
				this.details[this.currentItem].classList.remove('tw-opacity-0');
				this.textBlurbs.forEach(el=>el.classList.add('tw-opacity-0'));
				this.images.forEach((el,i)=>{
					if (i==this.currentItem){el.classList.add('tw-scale-105');return;}
					el.classList.add('tw-opacity-0');
				});
				this.nextButton.classList.add('tw-opacity-0');
				this.positionInfo.classList.add('tw-opacity-0');
				const toggleButton = this.images[this.currentItem].querySelector('.toggle-details');
				if (toggleButton){
					toggleButton.setAttribute('aria-label','Close Details');
					toggleButton.setAttribute('aria-expanded','true');
					toggleButton.querySelector('.open')?.classList.add('tw-hidden');
					toggleButton.querySelector('.close')?.classList.remove('tw-hidden');
				}
				this.updateA11y();
			}
			closeDetails() {
				if (!this.detailsOpen) return;
				this.detailsOpen = false;
				this.details[this.currentItem].classList.add('tw-opacity-0');
				this.textBlurbs.forEach(el=>el.classList.remove('tw-opacity-0'));
				this.images.forEach((el,i)=>{
					if (i==this.currentItem){el.classList.remove('tw-scale-105');return;}
					el.classList.remove('tw-opacity-0');
				});
				this.nextButton.classList.remove('tw-opacity-0');
				this.positionInfo.classList.remove('tw-opacity-0');
				const toggleButton = this.images[this.currentItem].querySelector('.toggle-details');
				if (toggleButton){
					toggleButton.setAttribute('aria-label','Show Details');
					toggleButton.setAttribute('aria-expanded','false');
					toggleButton.querySelector('.open')?.classList.remove('tw-hidden');
					toggleButton.querySelector('.close')?.classList.add('tw-hidden');
				}
				setTimeout(this.setTransitions.bind(this,{slow:true}),500);
				this.updateA11y();
			}
			setSelected(selected) {
				this.selectedImage().querySelector('video')?.pause();
				this.currentItem = selected;
				this.selectedImage().querySelector('video')?.play();
				this.setPositionInfo();
				this.textBlurbs.forEach((item,i)=>{
					const previousElementWidths = i <= selected ? this.calcLeftOffset(this.textBlurbs,i) : 0;
					const direction = i < selected ? -1 : i > selected ? 1 : 0;
					const viewWidth = document.documentElement.clientWidth;
					item.style.transform = 'var(--tw-transform)';
					item.style.setProperty('--tw-translate-x', `${direction * viewWidth - previousElementWidths}px`);
					item.classList.remove('tw-hidden');
				});
				this.images.forEach((item,i)=>{
					item.style.opacity = selected == i ? 1.0 : 0.3;
					item.style.transform = 'var(--tw-transform)';
					item.style.setProperty('--tw-scale-x', selected == i ? 1.0 : 0.8);
					item.style.setProperty('--tw-scale-y', selected == i ? 1.0 : 0.8);
					item.style.setProperty('--tw-translate-y', selected == i ? 0 : '-10%');
					item.style.setProperty('--tw-translate-x', selected < i ? '-10%' : selected > i ? '10%' : 0);
					item.setAttribute('aria-label',`Slide ${i+1} of ${this.itemCount}`);
				});
				this.moveImages();
				this.updateA11y();
			}
			moveImages() {
				const leftOffset = this.calcLeftOffset(this.images,this.currentItem);
				const element = this.imagesWrapper;
				element.style.transform = 'var(--tw-transform)';
				element.style.setProperty('--tw-translate-x', `${-leftOffset}px`);
			}
			selectedImage() { return this.images[this.currentItem]; }
			setTransitions({slow=true}) {
				this.applyTransitions({el:this.imagesWrapper,slow});
				this.images.forEach(el=>this.applyTransitions({el,slow}));
				this.textBlurbs.forEach(el=>this.applyTransitions({el,slow}));
				this.details.forEach(el=>this.applyTransitions({el,slow}));
			}
			applyTransitions({el,slow}) {
				const transitions = slow ? this.slowTransitions : this.quickTransitions;
				const removeTransitions = slow ? this.quickTransitions : this.slowTransitions;
				el.classList.add(...transitions);
				el.classList.remove(...removeTransitions);
			}
			setPositionInfo() {
				this.positionInfo.innerHTML = `${this.currentItem + 1} / ${this.itemCount}`;
			}
			calcLeftOffset(elements,i) {
				return Array.from(elements).slice(0,i).reduce((t,item)=>t+item.offsetWidth,0);
			}
			updateA11y() {
				for (let i=0;i<this.itemCount;i++) this.setA11yForSlideIndex(i, i===this.currentItem);
			}
			setA11yForSlideIndex(i,isCurrent) {
				const text = this.textBlurbs[i];
				const image = this.images[i];
				const details = this.details[i];
				;[text,image,details].forEach(el=>{
					if (!el) return;
					if (isCurrent) {
						el.removeAttribute('aria-hidden');
						el.removeAttribute('inert');
						this.restoreFocusable(el);
					} else {
						el.setAttribute('aria-hidden','true');
						el.setAttribute('inert','');
						this.removeFocusable(el);
					}
				});
			}
			removeFocusable(root) {
				root.querySelectorAll('a,button,input,select,textarea,[tabindex]').forEach(el=>{
					if (!el.hasAttribute('data-prev-tabindex') && el.hasAttribute('tabindex')) {
						el.setAttribute('data-prev-tabindex', el.getAttribute('tabindex'));
					}
					el.setAttribute('tabindex','-1');
					el.setAttribute('aria-hidden','true');
				});
			}
			restoreFocusable(root) {
				root.querySelectorAll('a,button,input,select,textarea,[tabindex]').forEach(el=>{
					if (el.hasAttribute('data-prev-tabindex')) {
						el.setAttribute('tabindex', el.getAttribute('data-prev-tabindex'));
						el.removeAttribute('data-prev-tabindex');
					} else {
						el.removeAttribute('tabindex');
					}
					el.removeAttribute('aria-hidden');
				});
			}
		}
	);
}
